// ============================================
// NOTIFICATION SERVICE — CLOSd
// ============================================
// All notifications route through Firebase Cloud Functions,
// which call the GHL API server-side (secure, no CORS issues).
// ============================================

import { functions } from "../firebase";
import { httpsCallable } from "firebase/functions";

export const sendNotification = httpsCallable(functions, "sendNotification");

const isDev = process.env.NODE_ENV === 'development';
const log = (...args) => isDev && console.log(...args);

// Helper to safely call the Cloud Function with logging
const callGHL = async (action, params) => {
  try {
    log(`[GHL] Sending ${action}...`);
    const result = await sendNotification({ action, ...params });
    log(`[GHL] ${action} result:`, result.data);
    return result.data;
  } catch (err) {
    console.error(`[GHL] ${action} failed:`, err.message);
    return { success: false, error: err.message };
  }
};

// ---- NEW DEAL NOTIFICATIONS (Email only) ----
// Groups all assigned tasks by team member, sends one email per person
export const sendNewDealNotifications = async (deal, tasks, teamMembers) => {
  log("=== NEW DEAL NOTIFICATION ===");
  log(`Deal: ${deal.propertyAddress || deal.clientName} (${deal.type})`);

  // Group tasks by assignee
  const tasksByAssignee = {};
  tasks.forEach((task) => {
    if (!task.assignedTo) return;
    if (!tasksByAssignee[task.assignedTo]) {
      tasksByAssignee[task.assignedTo] = [];
    }
    tasksByAssignee[task.assignedTo].push(task);
  });

  // Send one email per assignee
  for (const [assigneeName, assignedTasks] of Object.entries(tasksByAssignee)) {
    const member = teamMembers.find(
      (m) =>
        `${m.firstName} ${m.lastName}`.trim().toLowerCase() ===
        assigneeName.trim().toLowerCase()
    );

    if (!member) {
      log(`[SKIP] No member found for "${assigneeName}"`);
      continue;
    }

    const taskList = assignedTasks
      .map(
        (t) =>
          `  - ${t.name} (${t.theme})${t.dueDate ? ` — Due: ${t.dueDate}` : ""}`
      )
      .join("<br>");

    const dealName = deal.propertyAddress || deal.clientName || "Untitled Deal";

    await callGHL("sendEmail", {
      email: member.email,
      firstName: member.firstName || "",
      lastName: member.lastName || "",
      subject: `New Deal Created — ${dealName}`,
      body: `Hi ${member.firstName},<br><br>A new ${deal.type} deal has been created in CLOSd:<br><br>Deal: ${dealName}<br>Client: ${deal.clientName}<br>Type: ${deal.type}<br><br>Here are the tasks assigned to you:<br><br>${taskList}<br><br>Total tasks assigned to you: ${assignedTasks.length}<br><br><a href="https://app.getclosd.com" style="display:inline-block;background-color:#75BB2E;color:#ffffff;padding:12px 24px;text-decoration:none;border-radius:6px;font-weight:bold;">Log in to CLOSd</a><br><br>— The CLOSd Team`,
    });

    log(
      `[NEW DEAL] Email sent for ${member.firstName} ${member.lastName} (${assignedTasks.length} tasks)`
    );
  }

  log("=== END NEW DEAL NOTIFICATION ===");
};

// ---- TASK ASSIGNED NOTIFICATION (Email + SMS based on preference) ----
const sendTaskAssignedNotification = async (deal, task, teamMembers) => {
  log("=== TASK ASSIGNED NOTIFICATION ===");

  if (!task.assignedTo) return;

  const member = teamMembers.find(
    (m) =>
      `${m.firstName} ${m.lastName}`.trim().toLowerCase() ===
      task.assignedTo.trim().toLowerCase()
  );

  if (!member) {
    log(`[SKIP] No member found for "${task.assignedTo}"`);
    return;
  }

  const pref = member.notificationPreference || "email";
  const dealName = deal.propertyAddress || deal.clientName || "Untitled Deal";

  if (pref === "none") {
    log(`[TASK ASSIGNED] ${member.firstName} has notifications disabled — skipped`);
    return;
  }

  if (pref === "both" && member.phone) {
    await callGHL("sendBoth", {
      phone: member.phone,
      email: member.email,
      firstName: member.firstName || "",
      lastName: member.lastName || "",
      subject: `New Task Assigned — ${task.name}`,
      body: `Hi ${member.firstName},<br><br>You've been assigned a new task in CLOSd:<br><br>Task: ${task.name}<br>Deal: ${dealName}<br>Theme: ${task.theme}${task.dueDate ? `<br>Due: ${task.dueDate}` : ""}<br><br><a href="https://app.getclosd.com" style="display:inline-block;background-color:#75BB2E;color:#ffffff;padding:12px 24px;text-decoration:none;border-radius:6px;font-weight:bold;">Log in to CLOSd</a><br><br>— The CLOSd Team`,
      smsMessage: `CLOSd: You've been assigned "${task.name}" for ${dealName}.${task.dueDate ? ` Due: ${task.dueDate}.` : ""}`,
    });
  } else if (pref === "email" || (pref === "both" && !member.phone)) {
    await callGHL("sendEmail", {
      email: member.email,
      firstName: member.firstName || "",
      lastName: member.lastName || "",
      subject: `New Task Assigned — ${task.name}`,
      body: `Hi ${member.firstName},<br><br>You've been assigned a new task in CLOSd:<br><br>Task: ${task.name}<br>Deal: ${dealName}<br>Theme: ${task.theme}${task.dueDate ? `<br>Due: ${task.dueDate}` : ""}<br><br><a href="https://app.getclosd.com" style="display:inline-block;background-color:#75BB2E;color:#ffffff;padding:12px 24px;text-decoration:none;border-radius:6px;font-weight:bold;">Log in to CLOSd</a><br><br>— The CLOSd Team`,
    });
  } else if (pref === "sms" && member.phone) {
    await callGHL("sendSMS", {
      phone: member.phone,
      firstName: member.firstName || "",
      lastName: member.lastName || "",
      message: `CLOSd: You've been assigned "${task.name}" for ${dealName}.${task.dueDate ? ` Due: ${task.dueDate}.` : ""}`,
    });
  }

  log(`[TASK ASSIGNED] Notification sent for ${member.firstName}`);
  log("=== END TASK ASSIGNED NOTIFICATION ===");
};

// ---- BATCHED TASK ASSIGNMENT QUEUE ----
// Collects rapid task reassignments and sends one email per person per deal
// after 5 minutes of inactivity (so nobody gets spammed)
const taskAssignmentQueue = {};
const taskAssignmentTimers = {};

export const queueTaskAssignedNotification = (deal, task, teamMembers) => {
  if (!task.assignedTo) return;

  const dealId = deal.id || deal.clientName;
  const key = `${task.assignedTo}:::${dealId}`;

  if (!taskAssignmentQueue[key]) {
    taskAssignmentQueue[key] = { deal, tasks: [], teamMembers };
  }
  taskAssignmentQueue[key].tasks.push({ ...task });

  // Reset the 5-minute timer every time a new task comes in
  if (taskAssignmentTimers[key]) {
    clearTimeout(taskAssignmentTimers[key]);
  }

  taskAssignmentTimers[key] = setTimeout(() => {
    flushTaskAssignedQueue(key);
  }, 5 * 60 * 1000); // 5 minutes

  log(`[QUEUE] Queued "${task.name}" for ${task.assignedTo} (${dealId}) — timer reset to 5 min`);
};

const flushTaskAssignedQueue = async (key) => {
  const entry = taskAssignmentQueue[key];
  if (!entry || entry.tasks.length === 0) return;

  const { deal, tasks, teamMembers } = entry;
  delete taskAssignmentQueue[key];
  delete taskAssignmentTimers[key];

  // If only 1 task queued, send the normal single notification
  if (tasks.length === 1) {
    await sendTaskAssignedNotification(deal, tasks[0], teamMembers);
    return;
  }

  // Multiple tasks — send one batched notification
  const assigneeName = tasks[0].assignedTo;
  const member = teamMembers.find(
    (m) =>
      `${m.firstName} ${m.lastName}`.trim().toLowerCase() ===
      assigneeName.trim().toLowerCase()
  );

  if (!member) return;

  const pref = member.notificationPreference || "email";
  const dealName = deal.propertyAddress || deal.clientName || "Untitled Deal";

  if (pref === "none") return;

  const taskList = tasks
    .map(
      (t) =>
        `  - ${t.name} (${t.theme})${t.dueDate ? ` — Due: ${t.dueDate}` : ""}`
    )
    .join("<br>");

  const emailBody = `Hi ${member.firstName},<br><br>You've been assigned ${tasks.length} new tasks in CLOSd:<br><br>Deal: ${dealName}<br><br>${taskList}<br><br><a href="https://app.getclosd.com" style="display:inline-block;background-color:#75BB2E;color:#ffffff;padding:12px 24px;text-decoration:none;border-radius:6px;font-weight:bold;">Log in to CLOSd</a><br><br>— The CLOSd Team`;
  const smsText = `CLOSd: You've been assigned ${tasks.length} tasks for ${dealName}. Log in to CLOSd to view details.`;

  if (pref === "both" && member.phone) {
    await callGHL("sendBoth", {
      phone: member.phone,
      email: member.email,
      firstName: member.firstName || "",
      lastName: member.lastName || "",
      subject: `${tasks.length} New Tasks Assigned — ${dealName}`,
      body: emailBody,
      smsMessage: smsText,
    });
  } else if (pref === "email" || (pref === "both" && !member.phone)) {
    await callGHL("sendEmail", {
      email: member.email,
      firstName: member.firstName || "",
      lastName: member.lastName || "",
      subject: `${tasks.length} New Tasks Assigned — ${dealName}`,
      body: emailBody,
    });
  } else if (pref === "sms" && member.phone) {
    await callGHL("sendSMS", {
      phone: member.phone,
      firstName: member.firstName || "",
      lastName: member.lastName || "",
      message: smsText,
    });
  }

  log(
    `[BATCH TASK ASSIGNED] Sent ${tasks.length} tasks to ${member.firstName} for ${dealName}`
  );
};

// ---- DEAL CLOSd SMS TO SARAH & LINDSEY ----
export const sendDealClosedSMS = async (deal, closedByName) => {
  log("=== DEAL CLOSd NOTIFICATION ===");

  const dealName = deal.propertyAddress || deal.clientName || "Untitled Deal";

  await callGHL("dealClosed", {
    dealName,
    closedByName,
  });

  log(`[DEAL CLOSd] SMS sent to Sarah & Lindsey — Deal: ${dealName}`);
  log("=== END DEAL CLOSd NOTIFICATION ===");
};



